/**
 * Scoped-relationship FACT resolver.
 *
 * ROLE IN THE AUTHORITY PIPELINE (single-engine doctrine):
 *
 *   scopedRelationshipResolver (this module)
 *     → resolves valid scoped-relationship facts and candidate capability
 *       contributions for a user/resource
 *   scopedCapabilityResolver.ts (`canResolve`)
 *     → performs the FINAL allow/deny decision by combining canonical
 *       AccountRole, active membership, these relationship facts, access
 *       level, explicit rights, lifecycle state, requested scope and AAL/JIT
 *
 * This module is NOT a second authorization engine. Its helpers are for:
 *   - resolving accessible resource IDs (list building)
 *   - producing relationship facts consumed by the canonical resolver
 *   - deriving candidate capability contributions
 *   - display and filtering AFTER canonical authorization
 *
 * `hasScopedResourceAccess()` / `canAccessProjectViaScope()` are filtering and
 * fact helpers only. They MUST NOT be used as the independent final authority
 * check that guards a protected route, service call or data mutation — those
 * must call the canonical `canResolve` in `scopedCapabilityResolver.ts`.
 *
 * Doctrine:
 * - Account roles (organization_owner/admin/member, platform roles) determine
 *   organization- or control-plane-level administration only.
 * - Normal resource visibility (projects, companies, investments, reports) is
 *   NEVER granted by account role alone. It requires an active scoped
 *   relationship with a sufficient access level for the specific resource.
 * - `organization_member` with no scoped relationships must see nothing.
 * - Unknown/expired/suspended/revoked relationships must fail closed.
 * - `investor_representative` is scoped to an INVESTMENT, never directly to a
 *   project. Project visibility for investors is derived by the caller through
 *   an authoritative investment→project association, not by this resolver.
 *
 * This module is the client-side mirror of the server-side authority:
 * - `public.scoped_relationships` table
 * - `public.ax1_has_scoped_access` / `public.ax1_has_scoped_capability` RPCs
 * Server-side RLS remains authoritative for production.
 */
import type {
  ScopedAccessLevel,
  ScopedRelationshipType,
  ScopedResourceType,
  User,
  UserScopedRelationship,
} from '@/types';
import { hasScopedAccessLevel } from '@/types';
import type { Capability } from './capabilities';
import type { UserAuthState } from './scopedCapabilityResolver';

/**
 * Canonical relationship-type → capability mapping.
 * Replaces the deprecated `LEGACY_SCOPED_ROLE_CAPABILITIES` global-role table.
 * These capabilities only ever apply to the specific resource the relationship
 * is scoped to (project/company/investment/etc), never organization-wide.
 */
export const SCOPED_RELATIONSHIP_CAPABILITIES: Record<ScopedRelationshipType, Capability[]> = {
  // Workspace membership grants entry to the workspace only. Project / task /
  // asset surfaces require an explicit project or object-scoped assignment.
  workspace_member: ['calendar.view'],
  company_representative: ['projects.view', 'companies.view', 'files.view', 'tasks.view', 'calendar.view'],
  project_manager: [
    'users.view.collaborators', 'analytics.view.projects',
    'projects.view', 'projects.update',
    'projects.members.role.change', 'projects.members.remove',
    'files.view', 'files.create', 'files.update',
    'documents.restore', 'documents.version.restore', 'documents.sign',
    'tasks.view', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign',
    'companies.view', 'calendar.view', 'calendar.manage',
    'escalations.view', 'escalations.create', 'escalations.acknowledge',
    'escalations.resolve', 'escalations.cancel', 'gates.view', 'gates.create',
  ],
  project_contributor: [
    'projects.view', 'files.view', 'files.create',
    'tasks.view', 'tasks.update', 'calendar.view',
  ],
  partner_representative: [
    'users.view.collaborators', 'projects.view', 'files.view', 'files.create',
    'tasks.view', 'tasks.update', 'calendar.view', 'calendar.manage',
    'escalations.create', 'gates.view',
  ],
  vendor_representative: [
    'users.view.collaborators', 'projects.view', 'files.view', 'files.create',
    'tasks.view', 'tasks.update', 'calendar.view', 'calendar.manage',
    'gates.view',
  ],
  investor_representative: [
    'investor.dashboard.view', 'investor.investments.view', 'investor.milestones.view',
    'investor.distributions.view', 'investor.documents.view', 'investor.calendar.view',
    'investor.chat.access', 'investor.reports.view', 'investor.notifications.view',
    'investor.security.view', 'investor.settings.manage',
    'calendar.view', 'tasks.view', 'gates.view',
  ],
  investment_manager: [
    'investor.dashboard.view', 'investor.investments.view', 'investor.milestones.view',
    'investor.distributions.view', 'investor.documents.view', 'investor.reports.view',
    'investor.calendar.view', 'investor.chat.access', 'investor.notifications.view',
    'investor.security.view', 'investor.settings.manage',
    'investments.rights.view', 'gates.view', 'gates.review', 'calendar.view',
  ],
  reporting_recipient: [
    'investor.reports.view', 'investor.notifications.view',
    'investor.security.view', 'investor.settings.manage',
    'reports.view', 'calendar.view',
  ],
  decision_participant: ['gates.view', 'gates.review', 'calendar.view'],
  validator: ['gates.view', 'documents.sign', 'calendar.view'],
  observer: ['projects.view', 'calendar.view'],
};

function isRelationshipActive(relationship: UserScopedRelationship): boolean {
  if (relationship.status !== 'active') return false;

  const now = Date.now();
  const effectiveFrom = relationship.effectiveFrom ? Date.parse(relationship.effectiveFrom) : null;
  const effectiveUntil = relationship.effectiveUntil ? Date.parse(relationship.effectiveUntil) : null;

  if ((effectiveFrom !== null && !Number.isFinite(effectiveFrom))
    || (effectiveUntil !== null && !Number.isFinite(effectiveUntil))) {
    return false;
  }

  return (effectiveFrom === null || effectiveFrom <= now)
    && (effectiveUntil === null || effectiveUntil > now);
}

/** All active scoped relationships for a user, optionally narrowed to a resource type/id. */
export function getActiveScopedRelationships(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  resourceType?: ScopedResourceType,
  resourceId?: string,
): UserScopedRelationship[] {
  const relationships = user?.scopedRelationships ?? [];
  return relationships.filter((relationship) => {
    if (!isRelationshipActive(relationship)) return false;
    if (resourceType && relationship.resourceType !== resourceType) return false;
    if (resourceId && relationship.resourceId !== resourceId) return false;
    return true;
  });
}

/**
 * Whether the user has an active scoped relationship on the given resource
 * meeting the required access level.
 *
 * FILTERING / FACT HELPER ONLY. This is not a final authorization check.
 * Protected routes, services and mutations must go through the canonical
 * `canResolve` in `scopedCapabilityResolver.ts`. Fails closed on
 * missing/expired/unknown data.
 */
export function hasScopedResourceAccess(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  resourceType: ScopedResourceType,
  resourceId: string,
  requiredAccessLevel: ScopedAccessLevel = 'view',
): boolean {
  if (!resourceId) return false;
  return getActiveScopedRelationships(user, resourceType, resourceId)
    .some((relationship) => hasScopedAccessLevel(relationship.accessLevel, requiredAccessLevel));
}

/** All resource ids of a given type the user has active, sufficiently-privileged access to. */
export function getAccessibleResourceIds(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  resourceType: ScopedResourceType,
  requiredAccessLevel: ScopedAccessLevel = 'view',
): Set<string> {
  const ids = new Set<string>();
  for (const relationship of getActiveScopedRelationships(user, resourceType)) {
    if (hasScopedAccessLevel(relationship.accessLevel, requiredAccessLevel)) {
      ids.add(relationship.resourceId);
    }
  }
  return ids;
}

/**
 * Merged capability set for a specific resource, derived only from the user's
 * active scoped relationships on that resource (never from account role).
 */
export function getScopedCapabilitiesForResource(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  resourceType: ScopedResourceType,
  resourceId: string,
): Capability[] {
  const capabilities = new Set<Capability>();
  for (const relationship of getActiveScopedRelationships(user, resourceType, resourceId)) {
    for (const capability of SCOPED_RELATIONSHIP_CAPABILITIES[relationship.type] ?? []) {
      capabilities.add(capability);
    }
  }
  return [...capabilities];
}

/** Convenience wrapper: does the user have scoped (non-admin) view access to this project? */
export function canAccessProjectViaScope(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  projectId: string,
  requiredAccessLevel: ScopedAccessLevel = 'view',
): boolean {
  return hasScopedResourceAccess(user, 'project', projectId, requiredAccessLevel);
}

/** All project ids the user has scoped (non-admin) access to. */
export function getScopedAccessibleProjectIds(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  requiredAccessLevel: ScopedAccessLevel = 'view',
): Set<string> {
  return getAccessibleResourceIds(user, 'project', requiredAccessLevel);
}

/** All investment ids the user has active, sufficiently-privileged access to. */
export function getScopedAccessibleInvestmentIds(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  requiredAccessLevel: ScopedAccessLevel = 'view',
): Set<string> {
  return getAccessibleResourceIds(user, 'investment', requiredAccessLevel);
}

/**
 * Derive project ids an investor/investment relationship grants access to,
 * through an AUTHORITATIVE investment→project association supplied by the
 * caller. Investors are never scoped directly to projects; their project
 * visibility is always mediated by the investment link.
 *
 * @param resolveProjectIdsForInvestment maps an investment id to the project
 *        ids it is authoritatively linked to (e.g. from the investment
 *        repository or a verified link table). Returns an empty result for
 *        unknown investments (fail closed).
 */
export function getInvestmentDerivedProjectIds(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  resolveProjectIdsForInvestment: (investmentId: string) => Iterable<string>,
  requiredAccessLevel: ScopedAccessLevel = 'view',
): Set<string> {
  const projectIds = new Set<string>();
  for (const investmentId of getScopedAccessibleInvestmentIds(user, requiredAccessLevel)) {
    for (const projectId of resolveProjectIdsForInvestment(investmentId)) {
      if (projectId) projectIds.add(projectId);
    }
  }
  return projectIds;
}

/**
 * Build the relationship-facts map consumed by the canonical
 * `scopedCapabilityResolver` (`UserAuthState.relationships`).
 *
 * This is the hand-off point that keeps a SINGLE authority engine: relationship
 * facts resolved here are fed into `canResolve`, which makes the final decision.
 * The map is keyed `${relationshipType}:${resourceType}` → set of resource ids
 * with an active, at-least-`view` relationship.
 */
export function buildScopedRelationshipFacts(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): Record<string, string[]> {
  const facts: Record<string, string[]> = {};
  for (const relationship of getActiveScopedRelationships(user)) {
    const key = `${relationship.type}:${relationship.resourceType}`;
    (facts[key] ??= []).push(relationship.resourceId);
  }
  return facts;
}

/**
 * Build the canonical caller state consumed by `canResolve`.
 * Keeping this adapter beside relationship-fact construction prevents route
 * guards from drifting on suspension, role-plane, or explicit-right defaults.
 */
export function buildUserAuthState(user: User | null | undefined): UserAuthState {
  return {
    userId: user?.id,
    accountRole: user?.account_role,
    platformRole: user?.platform_role,
    workspaceRole: user?.workspace_role,
    scopedRelationshipFacts: user ? buildScopedRelationshipFacts(user) : undefined,
    explicitRights: [],
    suspended: user?.status === 'suspended',
  };
}

/**
 * Company ids represented through an active company-side relationship.
 * This is a relationship fact helper for display and data filtering; route and
 * action authorization remains the responsibility of the canonical resolver.
 */
export function getRepresentedRelationshipCompanyIds(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  relationshipType: Extract<ScopedRelationshipType, 'partner_representative' | 'vendor_representative'>,
): string[] {
  const ids = new Set<string>();
  for (const relationship of getActiveScopedRelationships(user)) {
    if (relationship.type !== relationshipType || !hasScopedAccessLevel(relationship.accessLevel, 'view')) {
      continue;
    }
    const companyId = relationship.representedCompanyId
      ?? (relationship.resourceType === 'company' ? relationship.resourceId : null);
    if (companyId) ids.add(companyId);
  }
  return [...ids];
}
